import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  private authenticationSubject = new BehaviorSubject<CognitoUserSession | null>(null);

  constructor() {
    Auth.configure({
      region: 'us-west-2',
      userPoolId: environment.userPoolId,
      userPoolWebClientId: environment.appClientId,
      identityPoolId: environment.identityPoolId,
      mandatorySignIn: true
    });

    // Initialize session check
    this.checkSession();

    // Listen for authentication events
    Hub.listen('auth', this.handleAuthEvents.bind(this));
  }

  // TODO: Add Type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAuthEvents(data: any) {
    const { payload } = data;
    if (['signIn', 'signUp', 'tokenRefresh'].includes(payload.event)) {
      this.checkSession();
    } else if (payload.event === 'signOut') {
      this.authenticationSubject.next(null);
    }
  }

  public isAuthenticated(): Observable<boolean> {
    return this.authenticationSubject.asObservable().pipe(
      map(session => !!session),
      catchError(() => of(false))
    );
  }

  public getSession(): Observable<CognitoUserSession | null> {
    return this.authenticationSubject.asObservable();
  }

  public checkSession(): void {
    Auth.currentSession()
      .then(session => this.authenticationSubject.next(session))
      .catch(() => this.authenticationSubject.next(null));
  }

  public getUserEmail(): Promise<string> {
    return (
      Auth.currentAuthenticatedUser()
        // Attributes.email for non SSO users, user.username for SSO users
        .then(user => user.attributes.email ?? user.username)
        .catch(err => {
          console.error('Error fetching user email:', err);
          return null;
        })
    );
  }

  public getUserId(): Promise<string> {
    return Auth.currentAuthenticatedUser()
      .then(user => user.attributes.sub) // 'sub' is the user ID in Cognito
      .catch(err => {
        console.error('Error fetching user ID:', err);
        return null;
      });
  }

  public async isAdmin(): Promise<boolean> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      return user.attributes['custom:isAdmin'] === 'true';
    } catch (err) {
      console.error('Error checking admin status:', err);
      return false;
    }
  }
}
